import { useTranslation } from 'react-i18next'
import {
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  Typography,
  useTheme
} from '@mui/material'
import { AuroraCirculationPumpStatus } from '../enums'

export interface AuroraCirculationPumpStatusControlProps {
  value: AuroraCirculationPumpStatus
  loading?: boolean
  onSubmit: (value: AuroraCirculationPumpStatus) => Promise<void>
}

export default function AuroraCirculationPumpStatusControl({
  value,
  loading = false,
  onSubmit
}: AuroraCirculationPumpStatusControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Indicates if the control is checked.
   */
  const isChecked = value === AuroraCirculationPumpStatus.ON

  /**
   * Handle the change event.
   */
  async function handleChange(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    try {
      await onSubmit(checked ? AuroraCirculationPumpStatus.ON : AuroraCirculationPumpStatus.OFF)
    } catch {
      // Ignore the error.
    }
  }

  return (
    <FormGroup
      sx={{
        p: 4,
        pr: 2,
        borderRadius: 2,
        background: theme.palette.background.default
      }}
    >
      <FormControlLabel
        value="bottom"
        control={
          <>
            {loading ? (
              <CircularProgress size={24} sx={{ mr: 4 }} />
            ) : (
              <Switch color="success" checked={isChecked} onChange={handleChange} />
            )}
          </>
        }
        label={
          <Stack direction="column" gap={0}>
            <Typography color="text.secondary">{t('circulationPump')}</Typography>
            <Typography color="text.primary" fontWeight={500}>
              {isChecked ? t('on') : t('off')}
            </Typography>
          </Stack>
        }
        labelPlacement="start"
        sx={{
          display: 'flex',
          color: 'text.secondary',
          width: '100%',
          m: 0,
          justifyContent: 'space-between'
        }}
      />
    </FormGroup>
  )
}
